import React, {useEffect, useState} from 'react';
import logo from '../assets/peticle-icon.png';
import {APP_STORE_URL, navLinks} from './content';

// `overlay` renders the header transparent over a dark hero until the page scrolls.
function Header({overlay = false}: { overlay?: boolean }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, {passive: true});
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const solid = !overlay || scrolled || open;

    return (
        <header
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                solid ? 'bg-surface/85 backdrop-blur-lg shadow-[0_1px_0_#E8E6DF]' : 'bg-transparent'
            }`}
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <div className={`flex items-center justify-between transition-all duration-300 ${solid ? 'h-16' : 'h-20'}`}>
                    <a href="/" title="Peticle" className="flex items-center gap-2.5">
                        <img className="rounded-xl shadow-sm" src={logo} width={36} height={36} alt="Peticle logo"/>
                        <span className={`text-xl font-extrabold tracking-tight transition-colors ${solid ? 'text-text' : 'text-white'}`}>
                            Peticle
                        </span>
                    </a>

                    <nav className="hidden md:flex items-center gap-7">
                        {navLinks.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                className={`text-sm font-semibold transition-colors ${
                                    solid ? 'text-soft hover:text-text' : 'text-white/80 hover:text-white'
                                }`}
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <a
                            href={APP_STORE_URL}
                            target="_blank"
                            rel="noreferrer"
                            className={`hidden sm:inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold transition-all hover:-translate-y-0.5 ${
                                solid ? 'bg-brand text-white hover:bg-brand-dark' : 'bg-white text-brand-dark hover:bg-brand-light'
                            }`}
                        >
                            Get the app
                        </a>
                        <button
                            type="button"
                            aria-label="Toggle menu"
                            aria-expanded={open}
                            onClick={() => setOpen(!open)}
                            className={`md:hidden p-2 rounded-lg ${solid ? 'text-text' : 'text-white'}`}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                {open
                                    ? <path d="M6 6l12 12M18 6L6 18"/>
                                    : <path d="M4 7h16M4 12h16M4 17h16"/>}
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <div className={`md:hidden overflow-hidden transition-[max-height] duration-300 ${open ? 'max-h-96' : 'max-h-0'}`}>
                <nav className="flex flex-col px-4 pb-4 gap-1">
                    {navLinks.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            onClick={() => setOpen(false)}
                            className="rounded-lg px-3 py-2.5 text-base font-semibold text-text hover:bg-brand-light"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a href="/data-settings" className="rounded-lg px-3 py-2.5 text-base font-semibold text-soft hover:bg-brand-light">
                        Data settings
                    </a>
                </nav>
            </div>
        </header>
    );
}

export default Header;
